import {
	LitElement,
	html,
	ifDefined,
	styles,
	css,
	injectDocumentStyles,
	defineElement,
} from "@mds/core";
import { inputCSS } from "./input.styles.js";

const lightDomStyles = css`
	.mds-body fieldset {
		display: flex;
		flex-direction: column;
		border: none;
		padding: 0;
		gap: 1rem;
	}
	.mds-body fieldset > div {
		text-align: end;
	}
`;
injectDocumentStyles([lightDomStyles], "mds-input");

/**
 * Layout Web Component
 * @slot - This element has a slot
 */
export class MdsInput extends LitElement {
	static get formAssociated() {
		return true;
	}

	static get styles() {
		return [...styles, inputCSS];
	}

	static get properties() {
		return {
			type: { type: String, reflect: true },
			label: { type: String, reflect: true },
			id: { type: String, reflect: true },
			name: { type: String, reflect: true },
			position: { type: String, reflect: true },
			value: { type: String, reflect: true },
			placeholder: { type: String, reflect: true },
			size: { type: Number },
			pattern: { type: String, reflect: true },
			errormessage: { type: String, reflect: true },
			message: { type: String, reflect: true },
			invalid: { type: Boolean, reflect: true },
			disabled: { type: Boolean, reflect: true },
			required: { type: Boolean, reflect: true },
			native: { type: Boolean, reflect: true },
			ondark: { type: Boolean, reflect: true },
		};
	}

	constructor() {
		super();
		this.internals = this.attachInternals();

		this.type = "text";
		this.id = undefined;
		this.name = "input";
		this.position = "top";
		this.label = undefined;
		this.pattern = undefined;
		this.placeholder = undefined;
		this.size = 3;
		this.message = undefined;
		this.errormessage = undefined;
		this.value = "";
		this.invalid = false;
		this.disabled = false;
		this.required = false;
		this.native = false;
	}

	firstUpdated(_changedProperties) {
		super.firstUpdated(_changedProperties);
		this.internals.setFormValue(this.value);
	}

	render() {
		return this.__inputTemplate();
	}

	__inputTemplate() {
		return html`
			<div class="mds-input" style="--input-size: ${this.size + 5}ch">
				${this.__labelTemplate()}
				<input
					placeholder="${ifDefined(this.placeholder)}"
					pattern="${ifDefined(this.pattern)}"
					@input="${this.__changedInput}"
					id="${ifDefined(this.id)}"
					size="${ifDefined(this.size)}"
					name="${this.name}"
					type="${this.type}"
					value="${this.value}"
					?disabled="${this.disabled}"
					?required="${this.required}"
					?ondark="${this.ondark}"
				/>
				${this.__getMessages()}
				<slot name="inline"></slot>
			</div>
		`;
	}

	__getMessages() {
		return this.message || this.invalid
			? html`
					<div class="messages">
						${this.__messageTemplate()}
						${this.__errorMessageTemplate()}
					</div>
			  `
			: html``;
	}

	__labelTemplate() {
		return this.label
			? html` <label for="${this.id}">${this.label}</label> `
			: html``;
	}

	__errorMessageTemplate() {
		return this.invalid
			? html` <p class="error">${this.errormessage}</p> `
			: html``;
	}

	__messageTemplate() {
		return this.message
			? html` <p class="message mds-body-small">${this.message}</p> `
			: html``;
	}
	__setValidity(event) {
		this.invalid = !event.target.checkValidity();
		if (!this.errormessage)
			this.errormessage = event.target.validationMessage;
		this.invalid
			? this.internals.setValidity(
					event.target.validity,
					this.errormessage
			  )
			: this.internals.setValidity({});
	}

	__changedInput(event) {
		const isValidTarget = /\binput\b/gi.test(event.target.tagName);
		if (this.disabled && !isValidTarget) {
			event.preventDefault();
			event.stopPropagation();
		} else {
			const state = event.target.checked ? "checked" : "";
			this.checked = !!event.target.checked;
			this.value = event.target.value;
			this.internals.setFormValue(this.value, state);
		}
		this.native && this.__setValidity(event);
	}
}
defineElement("mds-input", MdsInput);
